import { Divider, Typography } from "@mui/material";
import EvChargerComplete from "assets/ev-charger-complete.png";
import { AddressType } from "domain/core/types";
import { formatAddress } from "lib/util";
import React from "react";
import { Link } from "react-router-dom";
import Button from "ui/components/Button";
import {
  AppContainer,
  CompletionCardContainer,
  CompletionImage,
  CompletionNextStepsCard,
  CompletionNextStepsDetails,
  CompletionNextStepsDigit,
  CompletionRow,
  ContentWrapper,
} from "ui/components/StyledComponents";
import { PADDING } from "ui/theme";

import { EvPlanWithoutChargerCompletion } from "./EvPlanWithoutChargerCompletion";

interface NextStepsProps {
  address: AddressType;
  charger: boolean;
}

export const EvPlanCompletion: React.FC<NextStepsProps> = ({
  address,
  charger,
}) => {
  return (
    <>
      <Typography variant="h3" color="secondary">
        Next steps
      </Typography>
      <ContentWrapper>
        <Typography variant="body1">
          We’ll kick off the process for getting{" "}
          <Typography display="inline" variant="body2">
            {formatAddress(address)}{" "}
          </Typography>
          switched over to Z and will send you an email once things are
          underway. While we do this, these are the next steps within the
          process and what to expect. Thank you for choosing Z.
        </Typography>
      </ContentWrapper>

      {charger ? (
        <>
          <CompletionCardContainer>
            <CompletionNextStepsCard key={"card-next-steps-switching-in"}>
              <CompletionNextStepsDigit>
                <Typography variant="h3" component="p">
                  1.
                </Typography>
              </CompletionNextStepsDigit>
              <CompletionNextStepsDetails>
                <Typography variant="h3">Switching in</Typography>
                There's nothing else you need to handle before switching in.
                We'll handle switching you over from your previous provider -
                typically within 5 days.
              </CompletionNextStepsDetails>
            </CompletionNextStepsCard>
            <CompletionNextStepsCard key={"card-next-steps-evnex-quote"}>
              <CompletionNextStepsDigit>
                <Typography variant="h3" component="p">
                  2.
                </Typography>
              </CompletionNextStepsDigit>
              <CompletionNextStepsDetails>
                <Typography variant="h3">Evnex quote</Typography>
                Our partners at Evnex will reach out to check your eligibility
                and provide a quote for your Smart Charger install. They might
                ask for things like the charger location, the distance from your
                switchboard, as well as some photos of the area. There is no
                obligation to purchase a charger at this point in time.
              </CompletionNextStepsDetails>
            </CompletionNextStepsCard>
            <CompletionNextStepsCard key={"card-next-steps-charger-install"}>
              <CompletionNextStepsDigit>
                <Typography variant="h3" component="p">
                  3.
                </Typography>
              </CompletionNextStepsDigit>
              <CompletionNextStepsDetails>
                <Typography variant="h3">Charger install</Typography>
                Once you accept the quote from Evnex and confirm your repayment
                option, a contractor will contact you to arrange a time to
                install the charger. They will meet you at your property on the
                install date and ensure that your charger is safely installed
                and working properly.
              </CompletionNextStepsDetails>
            </CompletionNextStepsCard>
            <CompletionNextStepsCard key={"card-next-steps-start-charging"}>
              <CompletionNextStepsDigit>
                <Typography variant="h3" component="p">
                  4.
                </Typography>
              </CompletionNextStepsDigit>
              <CompletionNextStepsDetails>
                <Typography variant="h3">Start charging</Typography>
                Around your install date, you'll receive a welcome pack from
                Evnex with some info on how to use your Smart Charger, as well
                as access to your charger's monitoring app. This should be
                everything you need to get started with your charger.
              </CompletionNextStepsDetails>
            </CompletionNextStepsCard>
          </CompletionCardContainer>
          <Typography
            variant="body1"
            sx={{
              paddingBottom: PADDING.xLarge,
            }}
          >
            If you need to get in touch with our support centre we can identify
            your account by your name and phone number. You can now close this
            browser page.
          </Typography>
          <Divider />
          <CompletionRow>
            <CompletionImage
              srcSet={`${EvChargerComplete}`}
              src={EvChargerComplete}
              alt="EV charger"
            />
            <AppContainer>
              <Typography variant="h3" color="secondary">
                More about your charger
              </Typography>
              <Typography>
                Keen to start learning about how you can get the most out of
                your charger? Check out our EV blog - filled with hot tips on
                how to charge safely and efficiently.
              </Typography>
              <div>
                <Button
                  variant="contained"
                  id="button-charger-info"
                  component={Link}
                  to={`${process.env.REACT_APP_Z_WEBSITE_URL}about-z/news/category/ev-charging`}
                  target="_blank"
                >
                  Learn more
                </Button>
              </div>
            </AppContainer>
          </CompletionRow>
        </>
      ) : (
        <>
          <CompletionCardContainer>
            <CompletionNextStepsCard key={"card-next-steps-switching-in"}>
              <CompletionNextStepsDigit>
                <Typography variant="h3" component="p">
                  1.
                </Typography>
              </CompletionNextStepsDigit>
              <CompletionNextStepsDetails>
                <Typography variant="h3">Switching in</Typography>
                There's nothing else you need to handle before switching in.
                We'll handle switching you over from your previous provider -
                typically within 5 days.
              </CompletionNextStepsDetails>
            </CompletionNextStepsCard>
            <CompletionNextStepsCard key={"card-next-steps-evnex-quote"}>
              <CompletionNextStepsDigit>
                <Typography variant="h3" component="p">
                  2.
                </Typography>
              </CompletionNextStepsDigit>
              <CompletionNextStepsDetails>
                <Typography variant="h3">Start optimising</Typography>
                Now is a great time to start thinking about how you can change
                your habits to make the most of your half priced nights and your
                free power between 3am - 6am. Be sure to check your monthly bill
                to see how your usage is stacking up.
              </CompletionNextStepsDetails>
            </CompletionNextStepsCard>
          </CompletionCardContainer>
          <Typography
            variant="body1"
            sx={{
              paddingBottom: PADDING.xLarge,
            }}
          >
            If you need to get in touch with our support centre we can identify
            your account by your name and phone number. You can now close this
            browser page.
          </Typography>
          <Divider />
          <EvPlanWithoutChargerCompletion />
        </>
      )}
    </>
  );
};
